import React from 'react';
import { Typography } from '@mui/material';
import { countriesWithAgencyConfig } from 'features/auth/constants';
import { BucketId, Segmento } from './types';

/** Nombre de la agencia, del catálogo oficial (no duplicamos el mapeo). */
export const agencyName = (agencyId: number): string =>
	countriesWithAgencyConfig.find((a) => a.agencyId === agencyId)?.countryName ?? `Agencia ${agencyId}`;

export const SEGMENTOS: Segmento[] = ['Corpo', 'Wholesaler', 'Collaborators'];

export const SEGMENTO_LABEL: Record<Segmento, string> = {
	Corpo: 'Corporativo',
	Wholesaler: 'Mayorista',
	Collaborators: 'Colaboradores',
};

/** Buckets de vencido, del más nuevo al más viejo. */
export const BUCKETS_VENCIDO: { id: BucketId; label: string; corto: string }[] = [
	{ id: 'V_1_7', label: '1 a 7 días', corto: '1-7' },
	{ id: 'V_8_15', label: '8 a 15 días', corto: '8-15' },
	{ id: 'V_16_30', label: '16 a 30 días', corto: '16-30' },
	{ id: 'V_31_45', label: '31 a 45 días', corto: '31-45' },
	{ id: 'V_45_MAS', label: 'Más de 45 días', corto: '+45' },
];

/** Buckets de por vencer, del más próximo al más lejano. */
export const BUCKETS_POR_VENCER: { id: BucketId; label: string; corto: string }[] = [
	{ id: 'P_0_7', label: 'Vence en 7 días o menos', corto: '0-7' },
	{ id: 'P_8_15', label: 'Vence en 8 a 15 días', corto: '8-15' },
	{ id: 'P_16_30', label: 'Vence en 16 a 30 días', corto: '16-30' },
	{ id: 'P_31_45', label: 'Vence en 31 a 45 días', corto: '31-45' },
	{ id: 'P_46_60', label: 'Vence en 46 a 60 días', corto: '46-60' },
	{ id: 'P_60_MAS', label: 'Vence en más de 60 días', corto: '+60' },
];

// ─── Tooltips ────────────────────────────────────────────────────────────────

export const carteraInfoTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			¿Qué muestra esta pantalla? 💰
		</Typography>
		<Typography variant="body2" gutterBottom>
			La <strong>salud de la cartera</strong>: cuánto nos deben los clientes con cuenta corriente, hace cuánto, y qué
			tenemos pendiente de nuestro lado.
		</Typography>
		<Typography variant="body2">
			Todo en dólares, a la fecha de corte que elijas. Hacé click en cualquier importe para ver las facturas u órdenes
			que hay detrás.
		</Typography>
	</>
);

export const dosBloquesTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			Las columnas están en dos grupos ⚖️
		</Typography>
		<Typography variant="body2" gutterBottom>
			<strong>Deuda del cliente</strong> — acá sí se reclama: son facturas emitidas que el cliente todavía no pagó.
		</Typography>
		<Typography variant="body2" gutterBottom>
			<strong>Tarea nuestra</strong> — acá no hay que llamar a nadie: falta emitir una factura, devolver un saldo a
			favor, o imputar un recibo que quedó suelto.
		</Typography>
		<Typography variant="body2">
			Es la diferencia entre &laquo;a quién llamo&raquo; y &laquo;qué tengo que arreglar yo&raquo;.
		</Typography>
	</>
);

export const saldoCobranzaTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			Saldo de cobranza 🧾
		</Typography>
		<Typography variant="body2" gutterBottom>
			Lo que el cliente debe según sus <strong>órdenes</strong> (venta menos cobrado).
		</Typography>
		<Typography variant="body2">
			<strong>No</strong> es la suma de las otras columnas, y está bien que no lo sea: los cobros se aplican a órdenes y
			las deudas se documentan en facturas. Cuando no coinciden, la diferencia aparece en su columna.
		</Typography>
	</>
);

export const saldoAFavorTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			Saldo a favor del cliente 🔁
		</Typography>
		<Typography variant="body2" gutterBottom>
			Cobramos de más en alguna orden. Casi siempre es un recibo imputado a la orden equivocada; a veces hay que
			devolver la plata.
		</Typography>
		<Typography variant="body2">
			No se resta de la deuda a propósito: son dos situaciones distintas y cada una tiene su acción.
		</Typography>
	</>
);

export const diferenciaTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			¿Qué es la diferencia? 🔍
		</Typography>
		<Typography variant="body2" gutterBottom>
			Es lo que sobra o falta entre el saldo de las órdenes y lo que está facturado o pendiente de facturar.
		</Typography>
		<Typography variant="body2" gutterBottom>
			<strong>Negativa</strong>: hay facturas con saldo cuyo cobro ya entró en la orden. Falta vincular el recibo a la
			factura — no se le reclama al cliente.
		</Typography>
		<Typography variant="body2">
			<strong>Positiva</strong>: hay saldo de cobranza que no está facturado ni pendiente de facturar. Hay que
			investigarlo.
		</Typography>
	</>
);

export const multiClienteTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			Órdenes con más de un cliente facturable 🔀
		</Typography>
		<Typography variant="body2" gutterBottom>
			Estas órdenes tienen dos o más clientes que pueden recibir la factura, así que{' '}
			<strong>todavía no está decidido</strong> a quién corresponde ese saldo.
		</Typography>
		<Typography variant="body2">
			No se reparte entre los clientes para no inventar un número que después no se pueda defender. Suma al total de la
			agencia y queda acá como tarea: definir a quién se le factura.
		</Typography>
	</>
);

export const antiguedadTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			Antigüedad de la deuda ⏱️
		</Typography>
		<Typography variant="body2" gutterBottom>
			Cuanto más vieja, más difícil de cobrar. Abrí la columna para ver cómo se reparte por tramos.
		</Typography>
		<Typography variant="body2">
			Los tramos no se superponen: cada factura cae en uno solo, así los totales siempre cierran.
		</Typography>
	</>
);

export const fechaCorteTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			Fecha de corte 📅
		</Typography>
		<Typography variant="body2">
			El informe es una foto a una fecha. Cambiala para ver cómo estaba la cartera al cierre de un mes y conciliar con
			contabilidad.
		</Typography>
	</>
);
